import { TFunction } from "i18next";
import { chain, compact, map, range } from "lodash";
import { GenericFormFields } from "../../generic-form/GenericFormBody";
import { CreateTaskPayload, ScheduleCategoryItem } from "../../../models/job";
import { SupplierListItem } from "../../../models/supplier";
import { TaskAssignTo } from "../../../utils/options";
import { TeamMember } from "../../../models/team-member";

export const createTaskFields = (
  t: TFunction,
  startDate: string,
  endDate: string,
  suppliers: SupplierListItem[],
  openSupplierModal: () => void,
  team: TeamMember[],
  onAssignToChange: (type: string) => void,
  assignTo: TaskAssignTo,
  tasks: ScheduleCategoryItem[],
  customReminder: number,
  handleReminderCreate: (value: string) => void
): GenericFormFields<CreateTaskPayload> => {
  const supplierOptions = map(suppliers, (supplier) => ({
    value: supplier._id,
    label: supplier.business_name,
  }));

  const teamOptions = map(team, (member) => ({
    value: member._id,
    label: [member.first_name, member.last_name].join(" "),
  }));

  const taskOptions = map(tasks, (task) => ({
    value: task._id,
    label: task.name,
  }));

  const reminderOptions = chain([customReminder, ...range(1, 15)])
    .compact()
    .map((day) => ({
      value: day.toString(),
      label: t("schedule.daysBefore", { count: day }),
    }))
    .value();

  return {
    name: {
      type: "text",
      label: t("schedule.name"),
      placeholder: t("placeholders.name"),
      valueKey: "name",
      inputProps: {
        required: true,
      },
    },
    description: {
      type: "text",
      controlType: "textarea",
      label: t("schedule.description"),
      placeholder: t("schedule.description"),
      valueKey: "description",
      inputProps: {
        type: "text",
        rows: 2,
      },
    },
    dates: [
      {
        type: "date-picker",
        label: t("schedule.startDate"),
        placeholder: t("placeholders.startDate"),
        valueKey: "startDate",
        showError: true,
        datePickerProps: {
          // minDate: new Date(startDate),
          // maxDate: new Date(endDate),
        },
        inputProps: {
          required: true,
        },
      },
      {
        type: "date-picker",
        label: t("schedule.endDate"),
        placeholder: t("placeholders.endDate"),
        valueKey: "endDate",
        showError: true,
        datePickerProps: {
          // minDate: new Date(startDate),
          // maxDate: new Date(endDate),
        },
        inputProps: {
          required: true,
        },
      },
    ],
    progress: {
      type: "slider",
      label: t("schedule.progress"),
      placeholder: t("placeholders.progress"),
      valueKey: "progress",
      sliderProps: {
        showRangeValues: true,
        step: 10,
      },
    },
    assignType: {
      type: "button-select",
      label: t("common.assignTo"),
      valueKey: "assignTo",
      buttonSelectProps: {
        onChange: onAssignToChange,
        options: [
          {
            value: TaskAssignTo.TEAM,
            label: t("schedule.teamMember"),
          },
          {
            value: TaskAssignTo.SUPPLIER,
            label: t("schedule.supplier"),
          },
        ],
      },
    },
    assignTo:
      assignTo === TaskAssignTo.TEAM
        ? {
            type: "select",
            label: t("schedule.teamMember"),
            placeholder: t("schedule.select"),
            valueKey: "user",
            autocompleteProps: {
              options: teamOptions,
              isClearable: true,
            },
          }
        : {
            type: "select",
            label: t("schedule.supplier"),
            placeholder: t("schedule.select"),
            valueKey: "supplier",
            autocompleteProps: {
              options: supplierOptions,
              isClearable: true,
            },
            append: {
              onClick: openSupplierModal,
              icon: "add",
            },
          },
    daysBefore: {
      type: "creatable-select",
      label: t("schedule.reminder"),
      placeholder: t("schedule.select"),
      valueKey: "reminder.daysBefore",
      creatableProps: {
        options: reminderOptions,
        isClearable: true,
        autoSelectCreatedOption: true,
        isValidNewOption: (v: string) => Number(v) > 0,
        formatCreateLabel: (v: string) =>
          t("schedule.daysBefore", { count: Number(v) }),
        onCreateOption: handleReminderCreate,
      },
    },
    predecessor: {
      type: "select",
      label: t("schedule.predecessor"),
      placeholder: t("schedule.predecessor"),
      valueKey: "predecessor",
      autocompleteProps: {
        options: taskOptions,
        isClearable: true,
      },
    },
  };
};
